/**
 * Affichage des rendements.
 *
 * Les rendements arrivent du back en quintaux par hectare. Selon la culture on
 * les affiche en quintaux, en tonnes, ou - pour le lin fibre, dont le rendement
 * filasse se compte en kilos - en kilos par hectare.
 *
 * Unite, coefficient et precision vont ensemble : les dissocier, c'est afficher
 * une valeur dans une unite qui n'est pas la sienne, ou la meme valeur avec
 * deux nombres de decimales selon la page.
 *
 * ATTENTION, le lin fibre a DEUX conventions selon la provenance du chiffre, et
 * c'est le piege de ce module :
 *
 *  - l'objet `culture` (page Mes cultures) porte de la PAILLE. Sa filasse se
 *    deduit par le taux, avec `rendementFilasseEnKiloParHectare`.
 *  - les endpoints d'analyse (`rendement/...`, groupe, quartiles, evolution)
 *    renvoient deja de la FILASSE, exprimee en quintaux. Il n'y a plus qu'a
 *    passer aux kilos, sans retoucher au taux.
 *
 * `convertirRendement` traite le second cas, c'est-a-dire tout ce qui vient des
 * endpoints d'analyse. Lui appliquer le taux une seconde fois divise la valeur
 * par huit environ : un lin a 12 % s'affichait 183 kg/ha au lieu de 1 522.
 */

const CODES_TYPE_CULTURE_LIN_FIBRE = ['LFH', 'LFP']

const CODES_TYPE_CULTURE_MAIS_GRAIN = ['MG']

/** 1 quintal = 100 kg. */
const COEFFICIENT_QUINTAL_VERS_KILO = 100

/** 1 quintal = 0,1 tonne. */
const COEFFICIENT_QUINTAL_VERS_TONNE = 0.1

/**
 * Nombre de decimales affichees pour un rendement, quelle que soit l'unite.
 *
 * Une seule valeur, volontairement : c'est ce qui garantit qu'un meme rendement
 * se lit pareil sur les deux pages.
 */
const PRECISION_RENDEMENT = 2

export function isLinFibre (typeCulture) {
  return typeCulture != null && CODES_TYPE_CULTURE_LIN_FIBRE.includes(typeCulture.code)
}

export function isMaisGrain (typeCulture) {
  return typeCulture != null && CODES_TYPE_CULTURE_MAIS_GRAIN.includes(typeCulture.code)
}

/**
 * Precision a laquelle le rendement est affiche.
 *
 * Deux decimales pour toutes les cultures et toutes les unites. La fonction
 * garde son parametre pour que les appelants n'aient pas a savoir si la regle
 * depend de la culture, et pour qu'une regle par culture reste possible sans
 * toucher aux vues.
 */
// eslint-disable-next-line no-unused-vars
export function precisionRendement (typeCulture) {
  return PRECISION_RENDEMENT
}

/**
 * Mention accolee a l'unite pour les cultures dont le rendement affiche n'est
 * pas le rendement brut : le mais grain est ramene au sec, le lin fibre est
 * exprime en filasse et non en paille.
 *
 * @returns {string|null} la mention, ou null s'il n'y a rien a preciser
 */
export function mentionRendement (typeCulture) {
  if (isMaisGrain(typeCulture)) {
    return 'Rendement sec'
  }

  if (isLinFibre(typeCulture)) {
    return 'Rendement filasse'
  }

  return null
}

/**
 * Unite dans laquelle le rendement est presente a l'utilisateur.
 */
export function uniteRendement (typeCulture) {
  if (isLinFibre(typeCulture)) {
    return 'kg/ha'
  }

  if (typeCulture?.isUniteAfficheeTonneParHectare) {
    return 't/ha'
  }

  return 'qx/ha'
}

/**
 * Coefficient a appliquer a une valeur d'ANALYSE en quintaux par hectare pour
 * l'exprimer dans l'unite retournee par `uniteRendement`.
 *
 * A utiliser systematiquement avec elle, jamais l'une sans l'autre.
 *
 * Pour le lin, la valeur recue est deja de la filasse : le seul travail restant
 * est le passage des quintaux aux kilos. Le taux de filasse n'a rien a faire
 * ici, il a deja ete applique cote back.
 */
export function coefficientRendement (typeCulture) {
  if (isLinFibre(typeCulture)) {
    return COEFFICIENT_QUINTAL_VERS_KILO
  }

  if (typeCulture?.isUniteAfficheeTonneParHectare) {
    return COEFFICIENT_QUINTAL_VERS_TONNE
  }

  return 1
}

/**
 * Convertit une valeur d'analyse en quintaux par hectare vers l'unite
 * d'affichage.
 *
 * Reservee aux chiffres venant des endpoints d'analyse. Le rendement porte par
 * l'objet `culture` obeit a l'autre convention : voir l'en-tete du module.
 *
 * @param {number} valeurEnQuintauxParHectare valeur brute renvoyee par le back
 * @param {object} typeCulture culture concernee
 * @returns {number|null} la valeur convertie, ou null si elle est absente
 */
export function convertirRendement (valeurEnQuintauxParHectare, typeCulture) {
  if (valeurEnQuintauxParHectare == null) {
    return null
  }

  return valeurEnQuintauxParHectare * coefficientRendement(typeCulture)
}

/**
 * Rendement affiche pour une culture de la page Mes cultures, dans l'unite
 * retournee par `uniteRendement`.
 *
 * Pendant de `convertirRendement` pour l'autre convention : ici le rendement du
 * lin est de la PAILLE, et sa filasse se deduit par le taux porte par la
 * culture. Ailleurs les deux conventions coincident, et le meme coefficient
 * s'applique.
 *
 * @param {object} culture culture de l'adherent
 * @returns {number|null} la valeur convertie, ou null si elle est absente
 */
export function convertirRendementCulture (culture) {
  if (isLinFibre(culture?.typeCulture)) {
    return rendementFilasseEnKiloParHectare(culture.rendementEnQuintauxParHectare, culture.tauxFilasseEnPourcentage)
  }

  return convertirRendement(culture?.rendementEnQuintauxParHectare, culture?.typeCulture)
}

/**
 * Conversions paille <-> filasse du lin fibre.
 *
 * Le lin se saisit indifferemment en paille ou en filasse, mais il ne se STOCKE
 * que d'une seule facon : `rendementEnQuintauxParHectare` et `poidsEnTonne`
 * portent toujours la PAILLE, quel que soit le cote du switch. La filasse s'en
 * deduit par le taux.
 *
 * Les fonctions vont par paires exactement inverses. C'est la seule facon de
 * garantir qu'une valeur saisie, relue, redonne la valeur saisie : l'ancien code
 * multipliait par 0,1 a l'enregistrement puis divisait encore par 10 a la
 * relecture, d'ou les deux zeros perdus l'un apres l'autre.
 *
 * Un quintal de paille pese 100 kg, dont `taux` % de filasse. Un rendement de Q
 * quintaux de paille vaut donc Q x 100 x taux / 100, soit Q x taux kilos de
 * filasse par hectare - le facteur 100 s'annule, et c'est bien ce que fait deja
 * la carte de restitution en saisie paille.
 */

/** 1 tonne = 1000 kg. */
const COEFFICIENT_TONNE_VERS_KILO = 1000

/** Passe un poids des tonnes aux kilos, en laissant passer l'absence de valeur. */
export function enKilo (poidsEnTonne) {
  return poidsEnTonne == null ? null : poidsEnTonne * COEFFICIENT_TONNE_VERS_KILO
}

/** Passe un poids des kilos aux tonnes, en laissant passer l'absence de valeur. */
export function enTonne (poidsEnKilo) {
  return poidsEnKilo == null ? null : poidsEnKilo / COEFFICIENT_TONNE_VERS_KILO
}

/**
 * Rendement filasse, en kilos par hectare, d'une paille en quintaux par hectare.
 *
 * Point d'entree de la page Mes cultures, dont le rendement stocke est de la
 * paille. A ne pas confondre avec `convertirRendement`, qui part d'une filasse
 * deja calculee : enchainer les deux applique le taux deux fois.
 */
export function rendementFilasseEnKiloParHectare (rendementPailleEnQuintaux, tauxFilasseEnPourcentage) {
  if (rendementPailleEnQuintaux == null || !tauxFilasseEnPourcentage) {
    return null
  }

  return rendementPailleEnQuintaux * tauxFilasseEnPourcentage
}

/** Rendement paille, en quintaux par hectare, d'une filasse en kilos par hectare. */
export function rendementPailleEnQuintauxParHectare (rendementFilasseEnKilo, tauxFilasseEnPourcentage) {
  if (rendementFilasseEnKilo == null || !tauxFilasseEnPourcentage) {
    return null
  }

  return rendementFilasseEnKilo / tauxFilasseEnPourcentage
}

/** Poids de filasse, en tonnes, contenu dans un poids de paille en tonnes. */
export function poidsFilasseEnTonne (poidsPaille, tauxFilasseEnPourcentage) {
  if (poidsPaille == null || !tauxFilasseEnPourcentage) {
    return null
  }

  return poidsPaille * tauxFilasseEnPourcentage / 100
}

/** Poids de paille, en tonnes, correspondant a un poids de filasse en tonnes. */
export function poidsPailleEnTonne (poidsFilasse, tauxFilasseEnPourcentage) {
  if (poidsFilasse == null || !tauxFilasseEnPourcentage) {
    return null
  }

  return poidsFilasse * 100 / tauxFilasseEnPourcentage
}
